import { MonthPickerCalendar } from "@/components/ui/monthPickerCalendar"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/baseUi/popover"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/baseUi/select"
import { Text } from "@/components/ui/baseUi/text"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/baseUi/tooltip"
import { YearPicker } from "@/components/ui/yearPicker"
import { LoadingBlock } from "@/modules/boxDashboard/components/loadingBlock"
import {
  getDefaultCommitedClubPeriod,
  useCommitedClub,
  type CommitedClubPeriod,
  type CommitedClubPeriodType,
} from "@/modules/boxDashboard/modules/CommitedClub/hooks/useCommitedClub"
import {
  createCommitedClubBlockModel,
  type CommitedClubMemberModel,
} from "@/modules/boxDashboard/modules/CommitedClub/models/commitedClubModel"
import { ProfileAvatar } from "@/modules/profile/common/components/profileAvatar"
import attendeesEmptyIllustration from "@images/empty/attendees_empty.svg"
import { useCurrentBox } from "@modules/box/hooks/useBox"
import { word } from "@modules/core/utils/i18n"
import { cn } from "@modules/lib/utils"
import { useMemberSideBar } from "@modules/members/hooks/useMemberSidebar"
import dayjs from "dayjs"
import { ChevronDown, CircleHelp } from "lucide-react"
import { Suspense, useMemo, useState } from "react"

export const CommitedClubBlock = () => {
  const { result: box } = useCurrentBox()
  const [period, setPeriod] = useState<CommitedClubPeriod>(getDefaultCommitedClubPeriod())
  const [monthPickerYear, setMonthPickerYear] = useState(period.year)
  const [isMonthPickerOpen, setIsMonthPickerOpen] = useState(false)

  const YEARS_RANGE = useMemo(() => {
    const currentYear = dayjs().year()
    const boxCreationYear = box?.createdAt ? dayjs(box.createdAt).year() : currentYear - 4
    const yearsCount = currentYear - boxCreationYear + 1
    return Array.from({ length: yearsCount }, (_, i) => currentYear - i)
  }, [box])

  const handlePeriodTypeChange = (value: CommitedClubPeriodType) => {
    if (value === "YEAR") {
      setPeriod((prev) => ({ periodType: "YEAR", year: prev.year, month: undefined }))
      return
    }
    setPeriod((prev) => ({
      periodType: "MONTH",
      year: prev.year,
      month: prev.month ?? dayjs().month() + 1,
    }))
  }

  const handleYearChange = (yearValue: string) => {
    const year = Number(yearValue)
    setPeriod((prev) => ({
      ...prev,
      year,
      month: prev.periodType === "MONTH" ? (prev.month ?? dayjs().month() + 1) : undefined,
    }))
  }

  const handleMonthSelect = (monthIndex: number) => {
    setPeriod({ periodType: "MONTH", year: monthPickerYear, month: monthIndex + 1 })
    setIsMonthPickerOpen(false)
  }

  const getMonthLabel = () => {
    if (period.periodType === "MONTH" && period.month) {
      return `${dayjs()
        .month(period.month - 1)
        .format("MMMM")} ${period.year.toString()}`
    }
    return `${dayjs().format("MMMM")} ${period.year.toString()}`
  }

  const listItems = useMemo(
    () => [
      { label: word("boxDashboard.commitedClub.year"), value: "YEAR" },
      { label: word("boxDashboard.commitedClub.month"), value: "MONTH" },
    ],
    [],
  )

  const header = (
    <div className="flex w-full items-start justify-between gap-4 px-6 pt-6">
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <Text fontStyle="HM" className="text-textDefault-heading">
            {word("boxDashboard.commitedClub.title")}
          </Text>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger
                render={<CircleHelp className="size-6 text-textOnSurface-subtle hover:text-textOnSurface-muted" />}
              />
              <TooltipContent side="bottom" align="start" className="max-w-67 flex-col rounded-xl p-0 shadow-lg">
                <div className="flex w-full items-center gap-1 border-b border-interactive-border-neutral-default p-4">
                  <CircleHelp className="size-6 text-textOnSurface-subtle" />
                  <Text fontStyle="LM">{word("boxDashboard.commitedClub.title")}</Text>
                </div>
                <div className="p-4 pt-2">
                  <Text fontStyle="BM" className="wrap-break-word whitespace-break-spaces">
                    {word("boxDashboard.commitedClub.tooltip")}
                  </Text>
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <Text fontStyle="BM" className="text-textOnSurface-muted">
          {word("boxDashboard.commitedClub.subtitle")}
        </Text>
      </div>
    </div>
  )

  return (
    <div className="flex flex-col rounded-4xl bg-surface-foreground pb-6">
      {header}

      <div className="mr-6 flex items-center justify-end">
        <div className="flex items-center gap-0 rounded-full border border-interactive-border-neutral-default bg-surface-foreground">
          <Select
            items={listItems}
            value={period.periodType}
            onValueChange={(value) => {
              if (value === "YEAR" || value === "MONTH") {
                handlePeriodTypeChange(value)
              }
            }}
          >
            <SelectTrigger className="w-auto gap-1 rounded-l-full border-none bg-transparent px-4 py-2">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-2xl border-none bg-surface-background-float">
              {listItems.map((item) => (
                <SelectItem key={item.value} value={item.value} className="rounded-xl">
                  <Text fontStyle="LM">{item.label}</Text>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {period.periodType === "YEAR" ? (
            <YearPicker
              year={period.year}
              onYearChange={handleYearChange}
              years={YEARS_RANGE}
              className="w-auto gap-1 rounded-r-full border-none bg-surface-background px-4 py-2"
            />
          ) : (
            <Popover open={isMonthPickerOpen} onOpenChange={setIsMonthPickerOpen}>
              <PopoverTrigger
                className="px-0"
                render={
                  <button className="flex items-center gap-1 rounded-r-full bg-surface-background px-4 py-3">
                    <span className="text-sm">{getMonthLabel()}</span>
                    <ChevronDown className="size-4" />
                  </button>
                }
              />
              <PopoverContent className="w-64 rounded-2xl border-none bg-surface-background-float p-4" align="end">
                <MonthPickerCalendar
                  year={period.year}
                  month={period.month ? period.month - 1 : dayjs().month()}
                  onSelectMonth={handleMonthSelect}
                  onYearChange={setMonthPickerYear}
                  minDate={box?.createdAt ? new Date(box.createdAt) : undefined}
                />
              </PopoverContent>
            </Popover>
          )}
        </div>
      </div>

      <Suspense fallback={<LoadingBlock className="mx-6 mt-6 h-70" />}>
        <CommitedClubMembers period={period} />
      </Suspense>
    </div>
  )
}

function CommitedClubMembers({ period }: { period: CommitedClubPeriod }) {
  const { members } = useCommitedClub(period)
  const { navigateToMemberDetailsInfo } = useMemberSideBar()
  const model = createCommitedClubBlockModel({ members })

  return (
    <div className="flex flex-col gap-6 px-6 pt-6 md:flex-row">
      {model.isEmpty ? (
        <div className="flex w-full flex-col items-center justify-center gap-2 py-14">
          <img src={attendeesEmptyIllustration} alt="" className="h-43.75 w-74.5" />
          <Text fontStyle="LL" className="text-center">
            {word("boxDashboard.commitedClub.empty")}
          </Text>
        </div>
      ) : (
        <div className="flex w-full flex-row gap-2">
          <div className="flex w-full items-start justify-center md:w-1/2">
            <Podium
              members={model.podiumMembers}
              onClick={(id) => {
                navigateToMemberDetailsInfo(id, {})
              }}
            />
          </div>
          <div className="flex w-full flex-col justify-center md:w-1/2">
            {model.listMembers.map((member) => (
              <button
                key={member.id}
                className="flex cursor-pointer items-center justify-between rounded-xl p-1 hover:bg-surface-background-float"
                onClick={() => {
                  navigateToMemberDetailsInfo(member.id, {})
                }}
              >
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  <ProfileAvatar
                    className="size-8 shrink-0"
                    profile={{ avatar: member.avatar, username: member.fullName }}
                  />
                  <div className="flex min-w-0 flex-1 flex-col items-start overflow-hidden">
                    <Text fontStyle="LL" className="w-full truncate text-left text-textOnSurface-subtle">
                      {member.shortName}
                    </Text>
                    <Text fontStyle="LXS" className="text-textOnSurface-muted">
                      {member.rank}
                    </Text>
                  </div>
                </div>
                <Text fontStyle="LL" className="text-textOnSurface-subtle">
                  {member.sessionsCount}
                </Text>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function Podium({ members, onClick }: { members: CommitedClubMemberModel[]; onClick: (id: string) => void }) {
  const [first, second, third] = members
  return (
    <div className={cn("flex w-full items-end justify-center gap-0")}>
      {second && (
        <div className="relative top-1 left-5">
          <PodiumItem member={second} onClick={onClick} size={"size-20"} />
        </div>
      )}
      {first && (
        <div className="z-10">
          <PodiumItem member={first} onClick={onClick} size="size-32" />
        </div>
      )}
      {third && (
        <div className="relative top-1 right-5">
          <PodiumItem member={third} onClick={onClick} size={"size-16"} />
        </div>
      )}
    </div>
  )
}

function PodiumItem({
  member,
  onClick,
  size,
}: {
  member: CommitedClubMemberModel
  onClick: (id: string) => void
  size: string
}) {
  return (
    <button
      className="group flex cursor-pointer flex-col items-center gap-1"
      onClick={() => {
        onClick(member.id)
      }}
    >
      <div className="relative">
        <ProfileAvatar
          className={cn("border-2 border-textOnSurface-subtle group-hover:border-textOnSurface-primary-subtle", size)}
          profile={{ avatar: member.avatar, username: member.fullName }}
        />
        <div
          className={cn(
            "absolute -bottom-2.5 left-1/2 flex h-7 w-7 -translate-x-1/2 transform items-center justify-center rounded-full border-2 border-textOnSurface-subtle bg-surface-background group-hover:border-textOnSurface-primary-subtle",
          )}
        >
          <Text fontStyle="LL" className="mt-0.5 text-textOnSurface-subtle group-hover:text-primary-600">
            {member.rank}
          </Text>
        </div>
      </div>
      <Text
        fontStyle="LM"
        className="mt-4 max-w-18 truncate text-textOnSurface-subtle group-hover:text-textOnSurface-primary-subtle"
      >
        {member.shortName}
      </Text>
      <Text fontStyle="LM" className="text-textOnSurface-subtle group-hover:text-textOnSurface-primary-subtle">
        {member.sessionsCount}
      </Text>
    </button>
  )
}
